import * as Sentry from '@sentry/react';
import isExtendedWretchError from '@/modules/query-client/functions/isExtendedWretchError';
import { WretchError } from 'wretch/resolver';
import { merge, pick } from 'lodash-es';
import { match, P } from 'ts-pattern';
import { Buffer } from 'buffer';
import type { ExtendedWretchError } from '@/modules/query-client/types/ExtendedWretchError';
import isEmptyString from '@thisisdmg/tirecloud-patterns/v2/functions/isEmptyString';

/**
 * Capture an exception with Sentry
 * @param error - The error to capture
 * @param data - The additional data to capture
 * @param data.message - The message to capture
 * @param data.tags - The tags to capture
 * @param data.extras - The extras to capture
 * @param data.module - The module to capture
 * @param data.submodule - The submodule to capture
 */
export default function captureException(
  error: Error,
  {
    message = undefined,
    tags = {},
    extras = {},
    module = undefined,
    submodule = undefined,
  }: {
    message?: string;
    tags?: Record<string, string>;
    extras?: Record<string, unknown>;
    module?: string;
    submodule?: string;
  }
) {
  Sentry.withScope(async (scope) => {
    if (!isEmptyString(module)) scope.setTag('module', module);
    if (!isEmptyString(submodule)) scope.setTag('submodule', submodule);
    if (!isEmptyString(message)) scope.setExtra('message', message);

    for (const [key, value] of Object.entries(tags)) scope.setTag(key, value);
    for (const [key, value] of Object.entries(extras))
      scope.setExtra(key, value);

    if (
      error instanceof WretchError ||
      // wretch's fetchError throws a TypeError
      isFetchError(error)
    )
      logWretchError(error, scope);
    Sentry.captureException(error);
  });
}

/**
 * Check whether an error is a fetchError from wretch
 * @param error - The error to check
 * @returns Whether the error is a fetchError from wretch
 */
function isFetchError(error: Error): error is ExtendedWretchError {
  return (
    ['AuthLoginFetchError', 'UserGetFetchError'].includes(error.name) &&
    error instanceof TypeError
  );
}

/**
 * Add information from a WretchError to the Sentry scope
 * @param error - The WretchError to log
 * @param scope - The Sentry scope to add the information to
 */
function logWretchError(error: WretchError, scope: Sentry.Scope) {
  scope.setExtra(
    'http_request',
    merge(
      pick(error, ['url']),
      match(error)
        .with(P.when(isExtendedWretchError), P.when(isFetchError), (e) => {
          // eslint-disable-next-line @typescript-eslint/no-base-to-string
          const body = e.request._options.body?.toString();
          return {
            url: e.request._url,
            body:
              body !== undefined
                ? Buffer.from(body).toString('base64')
                : undefined,
            method: e.request._options.method,
            headers: e.request._options.headers,
            credentials: e.request._options.credentials,
          };
        })
        .otherwise(() => ({}))
    )
  );
  if (error.response !== undefined) {
    scope.setExtra(
      'http_response',
      merge(pick(error.response, ['status']), {
        headers: Object.fromEntries(error.response.headers.entries()),
        text:
          error.text !== undefined
            ? Buffer.from(error.text).toString('base64')
            : undefined,
        json:
          error.json !== undefined
            ? Buffer.from(JSON.stringify(error.json, null, 2)).toString(
                'base64'
              )
            : undefined,
      })
    );
  }
}
